import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import toast from 'react-hot-toast'
import {
  ClipboardList, ChevronRight, Activity, Play,
  UploadCloud, Link as LinkIcon, Sparkles, Download, Upload,
  Bot, CheckCircle2, XCircle, AlertTriangle, Filter,
} from 'lucide-react'
import { useDcStore as useGlobalDcStore } from '../stores/dcStore'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { PRODUCTION_WORKSPACE } from '../contexts/workspace.logic'
import { JiraTaskSelector } from '../components/integrations/JiraTaskSelector'
import { jiraChromeVisible, JIRA_OUTBOX_QUERY_KEY, type JiraOutboxStatus } from '../components/integrations/jiraChrome.logic'
import AiWorkOrderModal from '../components/integrations/AiWorkOrderModal'
import WorkOrderAiAssistant from '../components/layout/WorkOrderAiAssistant'
import { RollbackWoBadge, RollbackConfirmDialog } from '../components/RollbackWoBadge'
import { CorrectionWoBadge, CorrectionToast } from '../components/CorrectionWoBadge'
import { cancelResultMessage, mergeResultMessage, doneItemCount, woBadgeKind, ruPlural, ruWord, conflictHeadline } from '../components/woLifecycle.logic'

// ---------------------------------------------------------------------------
// Типы
// ---------------------------------------------------------------------------
interface WorkOrderItemPreview {
  item_type: string
  description: string
  metadata?: Record<string, any>
  has_unresolved: boolean
}

interface WorkOrderPreview {
  title: string
  description?: string
  datacenter_code?: string
  jira_ticket_id?: string
  items: WorkOrderItemPreview[]
  unresolved_notes?: string
  jira_metadata?: Record<string, any>
}

interface WorkOrderItem {
  id: string
  item_type: string
  description: string
  status: string
  sequence_number: number
  metadata_json?: Record<string, any>
}

interface WorkOrder {
  id: string
  datacenter_code: string
  title: string
  description: string
  status: string
  created_at: string
  jira_ticket_id?: string
  items: WorkOrderItem[]
  wo_type?: string | null
  parent_work_order_id?: string | null
}

// ---------------------------------------------------------------------------
// Константы
// ---------------------------------------------------------------------------
const STAGES = ['PLANNING', 'REVIEW', 'EXECUTION', 'MERGED']
const STAGE_LABELS: Record<string, string> = {
  PLANNING: 'Планирование',
  REVIEW: 'Согласование',
  EXECUTION: 'В работе',
  MERGED: 'Завершено',
  CANCELLED: 'Отменено',
}
const ITEM_TYPE_ICONS: Record<string, string> = {
  INSTALL_EQUIPMENT: '🖥️',
  REMOVE_EQUIPMENT: '🗑️',
  PATCH_CABLE: '🔌',
  UNPATCH_CABLE: '✂️',
  MOVE_EQUIPMENT: '📦',
  MOVE_RACK: '🗄️',
  CUSTOM: '📝',
}

// ---------------------------------------------------------------------------
// Компонент
// ---------------------------------------------------------------------------
export default function TasksPage() {
  const { dcCode: urlDcCode } = useParams()
  const { activeDcCode } = useGlobalDcStore()
  const { setActiveWorkspaceId } = useWorkspace()
  const navigate = useNavigate()
  const dcCode = urlDcCode || activeDcCode || ''
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  // Modal states
  const [isJiraSelectorOpen, setIsJiraSelectorOpen] = useState(false)
  const [isAiModalOpen, setIsAiModalOpen] = useState(false)
  const [activeAiWoId, setActiveAiWoId] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<WorkOrder | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [correctionToastId, setCorrectionToastId] = useState<string | null>(null)
  const [importTargetWoId, setImportTargetWoId] = useState<string | null>(null)

  const { data: jiraOutbox } = useQuery<JiraOutboxStatus>({
    queryKey: JIRA_OUTBOX_QUERY_KEY,
    queryFn: () => api.get('/api/integrations/jira/outbox/status').then(r => r.data),
    refetchInterval: 10000,
  })
  const showJira = jiraChromeVisible(jiraOutbox)

  // ConfirmDialog state (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; message: string; onConfirm: () => void
  }>({ open: false, message: '', onConfirm: () => {} })
  const openConfirm = (message: string, onConfirm: () => void) =>
    setConfirmDialog({ open: true, message, onConfirm })
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }))

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const fetchWorkOrders = (signal?: AbortSignal) => {
    if (!dcCode) return
    setLoading(true)
    setFetchError(null)
    api.get(`/api/datacenters/${dcCode}/impact-analysis/work-orders`, { signal })
      .then(res => setWorkOrders(res.data))
      .catch(err => {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return
        console.error(err)
        setFetchError('Не удалось загрузить наряды. Проверьте соединение.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const controller = new AbortController()
    fetchWorkOrders(controller.signal)
    return () => controller.abort()
  }, [dcCode])

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const handleUpdateItemStatus = async (woId: string, itemId: string, status: string) => {
    try {
      await api.patch(`/api/datacenters/${dcCode}/impact-analysis/work-orders/${woId}/items/${itemId}`, { status })
      toast.success('Шаг выполнен')
      fetchWorkOrders()
    } catch (error: any) {
      toast.error(`Ошибка: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleUpdateWOStatus = async (woId: string, status: string) => {
    try {
      await api.patch(`/api/datacenters/${dcCode}/impact-analysis/work-orders/${woId}`, { status })
      toast.success(`Статус изменён: ${STAGE_LABELS[status] || status}`)
      fetchWorkOrders()
    } catch (error: any) {
      const detail = error.response?.data?.detail
      if (status === 'EXECUTION') {
        if (typeof detail === 'string') {
          toast.error(`Нельзя взять в работу: ${detail}`, { duration: 6000 })
        } else if (Array.isArray(detail)) {
          toast.error(`Ошибка валидации: ${detail.map((d: any) => d.msg || d).join('; ')}`, { duration: 6000 })
        } else {
          toast.error(`Ошибка смены статуса: ${error.message}`, { duration: 5000 })
        }
      } else {
        toast.error(`Ошибка: ${detail || error.message}`)
      }
    }
  }

  const handleCommit = async (woId: string) => {
    try {
      const res = await api.post(`/api/datacenters/${dcCode}/impact-analysis/work-orders/${woId}/commit`)
      const correctionId = res.data?.correction_work_order_id ?? null
      if (correctionId) {
        setCorrectionToastId(correctionId)
      } else {
        toast.success(mergeResultMessage(null))
      }
      setActiveWorkspaceId(PRODUCTION_WORKSPACE)
      fetchWorkOrders()
    } catch (error: any) {
      const detail = error.response?.data?.detail
      if (typeof detail === 'string') {
        toast.error(`Ошибка применения: ${detail}`, { duration: 6000 })
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as array of {loc, msg, type}
        toast.error(`Ошибка валидации: ${detail.map((d: any) => d.msg || d).join('; ')}`, { duration: 6000 })
      } else {
        toast.error(`Ошибка commit: ${error.message}`, { duration: 5000 })
      }
    }
  }

  const handleCancel = (woId: string) => {
    const wo = workOrders.find(w => w.id === woId)
    if (!wo) return
    setCancelTarget(wo)
  }

  const confirmCancel = async () => {
    if (!cancelTarget) return
    setCancelLoading(true)
    try {
      const res = await api.post(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/${cancelTarget.id}/cancel`,
      )
      toast.success(cancelResultMessage(res.data?.rollback_work_order_id ?? null))
      setCancelTarget(null)
      fetchWorkOrders()
    } catch (error: any) {
      toast.error(`Ошибка: ${error.response?.data?.detail || error.message}`)
    } finally {
      setCancelLoading(false)
    }
  }

  const handleOpenSandbox = (woId: string) => {
    setActiveWorkspaceId(woId)
    toast.success(`Открыт черновик: ${woId}`)
    navigate(`/${dcCode}`)
  }

  /** Вызывается после подтверждения preview из Jira или AI */
  const handleConfirmJiraPreview = async (preview: WorkOrderPreview, targetDcCode?: string) => {
    const dc = targetDcCode || preview.datacenter_code || dcCode
    if (!dc) {
      toast.error('Не указан ЦОД — добавьте код ЦОДа в настройках импорта')
      return
    }
    try {
      const ticket = (preview.jira_ticket_id ?? '').trim()
      await api.post(`/api/datacenters/${dc}/impact-analysis/work-orders`, {
        title: preview.title,
        description: preview.description,
        datacenter_code: dc,
        jira_ticket_id: ticket || null,
        items: preview.items,
      }, ticket ? { headers: { 'X-Idempotency-Key': `jira-confirm-${ticket}` } } : undefined)
      toast.success('Наряд создан из задачи Jira!')
      fetchWorkOrders()
    } catch (error: any) {
      toast.error(`Ошибка создания: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleExportCableJournal = async (woId: string) => {
    try {
      const res = await api.get(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/${woId}/cable-journal/export`,
        { responseType: 'blob' }
      )
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `cable_journal_${woId.slice(0, 8)}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Кабельный журнал скачан')
    } catch (error: any) {
      toast.error(`Ошибка экспорта: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleImportClick = (woId: string) => {
    setImportTargetWoId(woId)
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !importTargetWoId) return

    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await api.post(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/${importTargetWoId}/cable-journal/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      const result = res.data
      if (result.parsed_count === 0) {
        toast.error('Файл прочитан, но строк не найдено. Проверьте формат таблицы.')
        return
      }

      // Применяем items к наряду через кастомный диалог
      const woIdSnapshot = importTargetWoId
      openConfirm(`Найдено ${result.parsed_count} кабелей. Добавить в наряд?`, async () => {
        await api.post(
          `/api/datacenters/${dcCode}/impact-analysis/work-orders/${woIdSnapshot}/cable-journal/apply`,
          result.items
        )
        toast.success(`Добавлено ${result.parsed_count} кабелей из журнала`)
        fetchWorkOrders()
      })
    } catch (error: any) {
      toast.error(`Ошибка импорта: ${error.response?.data?.detail || error.message}`)
    }

    // Сбрасываем input
    if (fileInputRef.current) fileInputRef.current.value = ''
    setImportTargetWoId(null)
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const allActiveOrders = workOrders.filter(wo => wo.status !== 'CANCELLED')
  const activeOrders = (filterStatus === 'ALL'
    ? allActiveOrders
    : allActiveOrders.filter(wo => wo.status === filterStatus)
  ).filter(wo => !searchQuery || wo.title?.toLowerCase().includes(searchQuery.toLowerCase()) || wo.jira_ticket_id?.toLowerCase().includes(searchQuery.toLowerCase()))
  const cancelledOrders = workOrders.filter(wo => wo.status === 'CANCELLED')

  return (
    <div className="p-6 animate-fade-in max-w-7xl mx-auto">
      {/* Hidden file input for XLSX import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.csv,.xls"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Header */}
      <div className="border-b border-border/40 pb-5 mb-6">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs font-bold text-text-muted uppercase tracking-[0.15em] mb-1">{dcCode?.toUpperCase()} · Наряды</p>
            <h1 className="text-3xl font-bold text-text-primary tracking-tight">Рабочие наряды</h1>
            <p className="text-sm text-text-muted mt-1">{ruPlural(workOrders.length, 'наряд', 'наряда', 'нарядов')} · активных {allActiveOrders.filter(w => w.status !== 'MERGED' && w.status !== 'CANCELLED').length}</p>
          </div>
          <div className="flex items-center gap-3">
            {showJira && (
            <button
              onClick={() => setIsJiraSelectorOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-bg-input text-text-primary border border-border hover:bg-bg-hover transition-all active:scale-95"
            >
              <LinkIcon className="w-4 h-4" />
              Импорт из Jira
            </button>
            )}
            <button
              onClick={() => setIsAiModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-accent text-white hover:bg-accent/90 transition-all active:scale-95 shadow-sm"
            >
              <Sparkles className="w-4 h-4" />
              Создать через AI
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-1 border-b border-border/40 mb-6 flex-wrap">
        {(['ALL', 'PLANNING', 'REVIEW', 'EXECUTION', 'MERGED'] as const).map(s => {
          const count = s === 'ALL' ? allActiveOrders.length : allActiveOrders.filter(w => w.status === s).length
          const isActive = filterStatus === s
          const label = s === 'ALL' ? 'Все' : STAGE_LABELS[s]
          return (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${
                isActive 
                  ? 'border-accent text-accent' 
                  : 'border-transparent text-text-muted hover:text-text-primary hover:border-border'
              }`}
            >
              {label} {count > 0 && <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                isActive ? 'bg-accent/15 text-accent' : 'bg-bg-input text-text-muted'
              }`}>{count}</span>}
            </button>
          )
        })}
        <div className="ml-auto pb-1 relative">
          <input
            type="text"
            placeholder="Поиск нарядов..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs bg-bg-input border border-border/40 rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/60 transition-colors w-48"
          />
          <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
        </div>
      </div>

      {/* Custom ConfirmDialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-bg-app/80 backdrop-blur-sm">
          <div className="bg-bg-panel border border-border rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 space-y-4">
            <p className="text-text-primary font-semibold text-center whitespace-pre-line text-sm leading-relaxed">{confirmDialog.message}</p>
            <div className="flex gap-3">
              <button
                onClick={closeConfirm}
                className="flex-1 py-2 rounded-xl border border-border text-text-muted hover:text-text-primary hover:border-border-hover transition-colors text-sm font-medium"
              >
                Отмена
              </button>
              <button
                onClick={() => { confirmDialog.onConfirm(); closeConfirm() }}
                className="flex-1 py-2 rounded-xl bg-status-critical/90 hover:bg-status-critical text-white font-semibold text-sm transition-colors"
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Work Orders list */}
      <div className={`space-y-6 ${activeAiWoId ? 'sm:pr-[420px]' : ''}`}>
        {/* Conflict Warning Banner */}
        {(() => {
          const executing = allActiveOrders.filter(wo => wo.status === 'EXECUTION')
          if (executing.length < 2) return null

          const rackMap: Record<string, { name: string; orders: typeof executing }> = {}

          executing.forEach(wo => {
            const touchedRacks = new Set<string>()
            const rackNames: Record<string, string> = {}

            wo.items.forEach(item => {
              const meta = item.metadata_json || {}
              const rackId = meta.rack_id ? String(meta.rack_id) : null
              const rackLabel = meta.rack_label || meta.rack_name || null
              const rackKey = rackId || rackLabel

              if (rackKey) {
                touchedRacks.add(rackKey)
                rackNames[rackKey] = rackLabel || (rackId ? `Стойка #${rackId}` : rackKey)
              }
            })

            touchedRacks.forEach(rackKey => {
              if (!rackMap[rackKey]) {
                rackMap[rackKey] = { name: rackNames[rackKey], orders: [] }
              }
              rackMap[rackKey].orders.push(wo)
            })
          })

          const conflictingRacks = Object.values(rackMap).filter(r => r.orders.length >= 2)
          // Fallback: show simpler warning if 2+ orders in EXECUTION but no rack metadata to compare
          const noRackData = executing.every(wo => wo.items.every(item => {
            const m = item.metadata_json || {}
            return !m.rack_id && !m.rack_label && !m.rack_name
          }))

          if (conflictingRacks.length === 0 && !noRackData) return null

          return (
            <div className="flex items-start gap-3 p-4 bg-status-warning/10 border border-status-warning/30 rounded-xl animate-fade-in">
              <AlertTriangle className="w-5 h-5 text-status-warning shrink-0 mt-0.5" />
              <div>
                {conflictingRacks.length > 0 ? (
                  <>
                    <p className="text-sm font-semibold text-status-warning">
                      {conflictHeadline(conflictingRacks.length)}
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      Внимание! Следующие стойки затрагиваются параллельно:
                     </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {conflictingRacks.map(r => (
                        <span key={r.name} className="text-[10px] font-mono px-2 py-0.5 bg-status-critical/10 text-status-critical rounded border border-status-critical/20">
                          {r.name} ({ruPlural(r.orders.length, 'наряд', 'наряда', 'нарядов')})
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-status-warning">
                      {ruPlural(executing.length, 'наряд', 'наряда', 'нарядов')} одновременно «В работе»
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      Инженеры могут одновременно вносить изменения в одно оборудование. Убедитесь, что наряды не пересекаются.
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {executing.map(wo => (
                        <span key={wo.id} className="text-[10px] font-mono px-2 py-0.5 bg-status-warning/20 text-status-warning rounded border border-status-warning/30">
                          {(wo.title ?? '').slice(0, 30)}{(wo.title ?? '').length > 30 ? '…' : ''}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        })()}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-40 rounded-xl skeleton opacity-50" />
            ))}
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-status-critical/10 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-status-critical" />
            </div>
            <p className="text-text-primary font-semibold">{fetchError}</p>
            <button onClick={() => fetchWorkOrders()} className="text-sm px-4 py-2 rounded-xl bg-accent/10 text-accent hover:bg-accent/20 transition-colors">
              Повторить
            </button>
          </div>
        ) : activeOrders.length === 0 && filterStatus !== 'ALL' ? (
          <div className="text-center py-16">
            <p className="text-text-muted text-sm mb-4">Нарядов со статусом «{STAGE_LABELS[filterStatus] || filterStatus}» нет</p>
            <button onClick={() => setFilterStatus('ALL')} className="text-sm text-accent hover:underline">Сбросить фильтр</button>
          </div>
        ) : workOrders.length === 0 ? (
          <div className="text-center py-24 px-8">
            <div className="w-16 h-16 rounded-2xl bg-bg-input border border-border/60 flex items-center justify-center mx-auto mb-6">
              <ClipboardList className="w-8 h-8 text-text-muted" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">Нарядов нет</h2>
            <p className="text-sm text-text-muted max-w-sm mx-auto mb-8">{showJira ? 'Создайте первый рабочий наряд: опишите задачу AI-ассистенту или импортируйте задачу из Jira.' : 'Создайте первый рабочий наряд: опишите задачу AI-ассистенту.'}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setIsAiModalOpen(true)}
                className="px-5 py-2.5 bg-accent text-white rounded-xl font-semibold text-sm hover:bg-accent/90 transition-colors flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Создать через AI
              </button>
            </div>
          </div>
        ) : (
          activeOrders.map(wo => <WorkOrderCard
            key={wo.id}
            wo={wo}
            dcCode={dcCode}
            onUpdateItem={handleUpdateItemStatus}
            onRefresh={fetchWorkOrders}
            onUpdateStatus={handleUpdateWOStatus}
            onCommit={handleCommit}
            onCancel={handleCancel}
            onOpenSandbox={handleOpenSandbox}
            onOpenAiAssistant={() => setActiveAiWoId(wo.id)}
            onExportCableJournal={() => handleExportCableJournal(wo.id)}
            onImportCableJournal={() => handleImportClick(wo.id)}
            onOpenConfirm={openConfirm}
          />)
        )}

        {/* Cancelled orders (collapsed) */}
        {cancelledOrders.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-sm text-text-muted hover:text-text-primary transition-colors py-2 flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
              <XCircle className="w-4 h-4" />
              Отменённые наряды ({cancelledOrders.length})
              <ChevronRight className="w-3.5 h-3.5 ml-auto transition-transform group-open:rotate-90" />
            </summary>
            <div className="space-y-3 mt-3">
              {cancelledOrders.map(wo => (
                <div key={wo.id} className="p-4 rounded-xl border border-border/30 bg-bg-panel/20 opacity-60">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-4 h-4 text-status-critical" />
                    <span className="font-medium text-text-secondary">{wo.title}</span>
                    <WoTypeBadge wo={wo} />
                    <span className="text-xs text-text-muted">{new Date(wo.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* AI Assistant sidebar — rendered in portal to escape transform containing block */}
      {activeAiWoId && createPortal(
        <>
          <div 
            className="fixed inset-0 z-40 bg-bg-app/40 sm:hidden"
            onClick={() => setActiveAiWoId(null)}
          />
          <div className="fixed right-0 top-[60px] bottom-0 w-full sm:w-[400px] z-50 shadow-2xl border-l border-border bg-bg-panel">
            <WorkOrderAiAssistant
              dcCode={dcCode}
              woId={activeAiWoId}
              workOrderItems={workOrders.find(w => w.id === activeAiWoId)?.items}
              readOnly={workOrders.find(w => w.id === activeAiWoId)?.status === 'PLANNING'}
              onClose={() => setActiveAiWoId(null)}
              onActionComplete={fetchWorkOrders}
            />
          </div>
        </>,
        document.body
      )}

      {/* Modals */}
      {showJira && (
      <JiraTaskSelector
        isOpen={isJiraSelectorOpen}
        onClose={() => setIsJiraSelectorOpen(false)}
        onConfirmPreview={handleConfirmJiraPreview}
        defaultDcCode={dcCode}
      />
      )}

      <AiWorkOrderModal
        isOpen={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        dcCode={dcCode}
        onCreated={fetchWorkOrders}
      />

      {cancelTarget && (
        <RollbackConfirmDialog
          doneItemCount={doneItemCount(cancelTarget.items)}
          woTitle={cancelTarget.title}
          isLoading={cancelLoading}
          onCancel={() => setCancelTarget(null)}
          onConfirm={confirmCancel}
        />
      )}
      {correctionToastId && (
        <CorrectionToast
          correctionWorkOrderId={correctionToastId}
          onDismiss={() => setCorrectionToastId(null)}
        />
      )}
    </div>
  )
}

function WoTypeBadge({ wo }: { wo: WorkOrder }) {
  const kind = woBadgeKind(wo)
  const parentId = wo.parent_work_order_id || wo.id
  if (kind === 'ROLLBACK') return <RollbackWoBadge parentWorkOrderId={parentId} />
  if (kind === 'CORRECTION') return <CorrectionWoBadge parentWorkOrderId={parentId} />
  return null
}

// ---------------------------------------------------------------------------
// WorkOrderCard sub-component
// ---------------------------------------------------------------------------
interface WorkOrderCardProps {
  wo: WorkOrder
  dcCode: string
  onUpdateItem: (woId: string, itemId: string, status: string) => void
  onRefresh: () => void
  onUpdateStatus: (woId: string, status: string) => void
  onCommit: (woId: string) => void
  onCancel: (woId: string) => void
  onOpenSandbox: (woId: string) => void
  onOpenAiAssistant: () => void
  onExportCableJournal: () => void
  onImportCableJournal: () => void
  onOpenConfirm: (message: string, onConfirm: () => void) => void
}

function WorkOrderCard({
  wo, dcCode, onUpdateItem, onRefresh, onUpdateStatus, onCommit, onCancel,
  onOpenSandbox, onOpenAiAssistant, onExportCableJournal, onImportCableJournal,
  onOpenConfirm,
}: WorkOrderCardProps) {
  // E-WO-03: SKIPPED is also a valid final status
  const allDone = wo.items.length > 0 && wo.items.every(i => i.status === 'DONE' || i.status === 'SKIPPED')
  const hasCableItems = wo.items.some(i => i.item_type === 'PATCH_CABLE' || i.item_type === 'UNPATCH_CABLE')
  const hasUnresolved = wo.items.some(i => {
    const meta = i.metadata_json || {}
    return meta.catalog_match_confidence === 'none' ||
      meta.rack_match_confidence === 'none' ||
      meta.needs_clarification_reason
  })

  const { data: racksList } = useQuery({
    queryKey: ['racks', dcCode],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/racks`).then(res => res.data),
    enabled: !!dcCode
  })

  const { data: catalogList } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.get('/api/equipment/catalog').then(res => res.data)
  })

  // Inline item editor state
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editFields, setEditFields] = useState<{
    catalog_id: string; rack_id: string; u_position: string
  }>({ catalog_id: '', rack_id: '', u_position: '' })
  const [saving, setSaving] = useState(false)

  const openItemEditor = (item: WorkOrderItem) => {
    const meta = item.metadata_json || {}
    setEditFields({
      catalog_id: meta.catalog_id ? String(meta.catalog_id) : '',
      rack_id: meta.rack_id ? String(meta.rack_id) : '',
      u_position: meta.u_position != null ? String(meta.u_position) : '',
    })
    setEditingItemId(item.id)
  }

  const handleSaveItemMeta = async (item: WorkOrderItem) => {
    const meta = item.metadata_json || {}
    const selectedCatalog = catalogList?.find((c: any) => String(c.id) === editFields.catalog_id)
    const selectedRack = racksList?.find((r: any) => String(r.id) === editFields.rack_id)

    const payload = {
      metadata_json: {
        ...meta,
        ...(editFields.catalog_id ? { 
          catalog_id: Number(editFields.catalog_id),
          resolved_catalog_name: selectedCatalog ? `${selectedCatalog.manufacturer} ${selectedCatalog.model}` : meta.resolved_catalog_name
        } : {}),
        ...(editFields.rack_id ? { 
          rack_id: Number(editFields.rack_id),
          rack_name: selectedRack ? (selectedRack.name || selectedRack.label || `Стойка #${selectedRack.id}`) : meta.rack_name
        } : {}),
        ...(editFields.u_position !== '' ? { u_position: Number(editFields.u_position) } : {}),
      },
    }
    setSaving(true)
    try {
      await api.patch(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/${wo.id}/items/${item.id}`,
        payload
      )
      toast.success('Шаг обновлён')
      setEditingItemId(null)
      onRefresh() // pure data refresh, no extra PATCH
    } catch (err: any) {
      toast.error(`Ошибка: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`group border border-border/60 bg-bg-panel/40 backdrop-blur-xl rounded-2xl overflow-hidden transition-all duration-200 shadow-sm hover:shadow-md hover:border-border/80 ${
      wo.status === 'PLANNING' ? 'border-l-2 border-l-accent/60' :
      wo.status === 'REVIEW' ? 'border-l-2 border-l-status-warning/60' :
      wo.status === 'EXECUTION' ? 'border-l-2 border-l-status-normal/60' :
      wo.status === 'CANCELLED' ? 'border-l-2 border-l-status-critical/60' :
      'border-l-2 border-l-border'
    }`}>
      <div className="p-6">
        <div className="flex flex-col mb-5">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 text-[11px] font-bold rounded-md tracking-wider uppercase ${
              wo.status === 'PLANNING' ? 'text-blue-400 bg-blue-400/10 border border-blue-400/20' :
              wo.status === 'REVIEW' ? 'text-status-warning bg-status-warning/10 border border-status-warning/20' :
              wo.status === 'EXECUTION' ? 'text-status-normal bg-status-normal/10 border border-status-normal/20' :
              wo.status === 'CANCELLED' ? 'text-status-critical bg-status-critical/10 border border-status-critical/20' :
              'text-text-muted bg-bg-input border border-border'
            }`}>
              {STAGE_LABELS[wo.status] || wo.status}
            </span>
            <h3 className="text-xl font-bold text-text-primary tracking-tight">{wo.title}</h3>
            <WoTypeBadge wo={wo} />
            {hasUnresolved && (
              <div className="px-2 py-0.5 text-xs font-semibold rounded-md bg-status-warning/10 text-status-warning flex items-center gap-1 border border-status-warning/20">
                <AlertTriangle className="w-3 h-3" /> Уточнить
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-text-muted">
            <span className="font-mono bg-bg-input px-1.5 py-0.5 rounded border border-border/50 text-[10px]">WO-{(wo.id ?? '').split('-')[0].toUpperCase()}</span>
            {wo.jira_ticket_id && <span>·</span>}
            {wo.jira_ticket_id && (
              <span className="flex items-center gap-1 font-semibold text-text-secondary"><LinkIcon className="w-3 h-3"/> {wo.jira_ticket_id}</span>
            )}
            <span>·</span>
            <span>Создан {new Date(wo.created_at).toLocaleDateString()}</span>
            <span>·</span>
            <span>{ruPlural(wo.items.length, 'шаг', 'шага', 'шагов')}</span>
          </div>
        </div>
        
        {wo.description && (
          <p className="text-sm text-text-muted italic max-w-3xl mb-5 line-clamp-2">{wo.description}</p>
        )}

        {/* Stage pipeline */}
        <div className="flex bg-bg-input/20 rounded-xl text-xs font-bold border border-border/40 overflow-hidden mb-6">
          {STAGES.map((stage, idx) => {
            const isActive = wo.status === stage
            const isPast = STAGES.indexOf(wo.status) > idx
            return (
              <div
                key={stage}
                className={`flex-1 flex items-center justify-center py-2 relative transition-all ${
                  isActive ? 'bg-text-primary text-bg-app' : isPast ? 'text-text-primary bg-accent/5' : 'text-text-muted'
                }`}
              >
                {isPast && <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 opacity-80" />}
                {STAGE_LABELS[stage]}
                {idx < STAGES.length - 1 && (
                  <div className="absolute right-0 top-0 bottom-0 flex items-center z-10 translate-x-[2px]">
                    <ChevronRight className={`w-4 h-4 ${isActive ? 'text-bg-app' : 'text-border'}`} />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Actions row */}
        <div className="flex flex-wrap gap-2 items-center">
            {wo.status === 'PLANNING' && (
              <button
                onClick={() => onOpenConfirm(
                  'Отправить наряд на согласование менеджеру? После этого статус изменится на «На согласовании» и начнётся процесс проверки.',
                  () => onUpdateStatus(wo.id, 'REVIEW')
                )}
                className="text-sm bg-text-primary hover:bg-white text-bg-app px-4 py-2 rounded-xl transition-all font-bold active:scale-95 shadow-sm"
              >
                На согласование
              </button>
            )}
            {wo.status === 'REVIEW' && (
              <button
                onClick={() => onOpenConfirm(
                  'Перевести наряд в статус "В работе"? После этого инженеры начнут выполнение. Убедитесь, что все шаги согласованы.',
                  () => onUpdateStatus(wo.id, 'EXECUTION')
                )}
                className="text-sm bg-text-primary hover:bg-white text-bg-app px-4 py-2 rounded-xl transition-all font-bold active:scale-95 shadow-sm"
              >
                Взять в работу
              </button>
            )}
            {wo.status === 'EXECUTION' && (
              <button
                onClick={() => onOpenConfirm(
                  `Применить наряд в master?\n\nЭто действие необратимо.`,
                  () => onCommit(wo.id)
                )}
                disabled={!allDone}
                title={!allDone ? 'Все шаги должны быть выполнены или пропущены' : 'Применить изменения в master'}
                className={`text-sm px-5 py-2 rounded-xl transition-all font-bold active:scale-95 flex items-center gap-2 ${
                  allDone
                    ? 'bg-status-normal hover:bg-status-normal/90 text-white shadow-sm'
                    : 'bg-bg-input text-text-muted border border-border cursor-not-allowed'
                }`}
              >
                <UploadCloud className="w-4 h-4" /> Применить в master
              </button>
            )}

            {/* Open sandbox */}
            {wo.status !== 'MERGED' && wo.status !== 'CANCELLED' && (
              <button
                onClick={() => onOpenSandbox(wo.id)}
                className="text-sm bg-bg-input hover:bg-bg-hover text-text-primary px-4 py-2 rounded-xl transition-all font-semibold active:scale-95 flex items-center gap-2 border border-border/60"
              >
                <Play className="w-4 h-4 text-text-muted" /> Черновик
              </button>
            )}

            {/* AI Assistant */}
            {(wo.status === 'EXECUTION' || wo.status === 'PLANNING') && (
              <button
                onClick={onOpenAiAssistant}
                className="text-sm bg-bg-input hover:bg-bg-hover text-text-primary px-4 py-2 rounded-xl transition-all font-semibold active:scale-95 flex items-center gap-2 border border-border/60"
              >
                <Bot className="w-4 h-4 text-text-muted" /> {wo.status === 'EXECUTION' ? 'AI Ассистент' : 'Спросить AI'}
              </button>
            )}

            {/* Cable journal buttons */}
            {hasCableItems && (
              <button
                onClick={onExportCableJournal}
                title="Скачать кабельный журнал XLSX"
                className="text-sm bg-bg-input hover:bg-bg-hover text-text-primary px-3 py-2 rounded-xl transition-all active:scale-95 flex items-center gap-2 border border-border/60"
              >
                <Download className="w-4 h-4" /> Журнал
              </button>
            )}
            {wo.status !== 'MERGED' && wo.status !== 'CANCELLED' && (
              <button
                onClick={onImportCableJournal}
                title="Импортировать кабельный журнал XLSX"
                className="text-sm bg-bg-input hover:bg-bg-hover text-text-primary px-3 py-2 rounded-xl transition-all active:scale-95 flex items-center gap-2 border border-border/60"
              >
                <Upload className="w-4 h-4" />
              </button>
            )}

            {/* Cancel */}
            {wo.status !== 'MERGED' && wo.status !== 'CANCELLED' && (
              <button
                onClick={() => onCancel(wo.id)}
                className="text-sm ml-auto bg-transparent hover:bg-status-critical/10 text-text-muted hover:text-status-critical px-3 py-2 rounded-xl transition-all active:scale-95"
                title="Отменить наряд"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
        </div>
      </div>

      {/* Executive Summary – shown when WO is pending manager approval */}
      {wo.status === 'REVIEW' && (
        <div className="px-6 py-4 bg-bg-input/30 border-t border-border/30">
          <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3 flex items-center gap-2">
            <Activity className="w-3 h-3" /> Итоговая сводка для согласования
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-bg-panel/60 rounded-xl border border-border/40 p-3 text-center">
              <div className="text-2xl font-bold text-text-primary">{wo.items.length}</div>
              <div className="text-[10px] text-text-muted mt-0.5">{ruWord(wo.items.length, 'шаг', 'шага', 'шагов')}</div>
            </div>
            <div className="bg-bg-panel/60 rounded-xl border border-border/40 p-3 text-center">
              <div className="text-2xl font-bold text-accent">
                {wo.items.filter(i => i.item_type === 'INSTALL_EQUIPMENT').length}
              </div>
              <div className="text-[10px] text-text-muted mt-0.5">установок</div>
            </div>
            <div className="bg-bg-panel/60 rounded-xl border border-border/40 p-3 text-center">
              <div className="text-2xl font-bold text-text-secondary">
                {wo.items.filter(i => i.item_type === 'PATCH_CABLE' || i.item_type === 'UNPATCH_CABLE').length}
              </div>
              <div className="text-[10px] text-text-muted mt-0.5">коммутаций</div>
            </div>
            <div className={`rounded-xl border p-3 text-center ${
              hasUnresolved
                ? 'bg-status-warning/10 border-status-warning/30'
                : 'bg-status-normal/10 border-status-normal/30'
            }`}>
              <div className={`text-2xl font-bold ${hasUnresolved ? 'text-status-warning' : 'text-status-normal'}`}>
                {wo.items.filter(i => {
                  const m = i.metadata_json || {}
                  return m.catalog_match_confidence === 'none' || m.rack_match_confidence === 'none' || !!m.needs_clarification_reason
                }).length}
              </div>
              <div className="text-[10px] text-text-muted mt-0.5">не решено</div>
            </div>
          </div>
          {hasUnresolved && (
            <p className="text-xs text-status-warning mt-3 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              Есть нерешённые шаги. Уточните перед согласованием.
            </p>
          )}
        </div>
      )}

      {/* Checklist */}
      <details className="group border-t border-border/30 open:bg-bg-app/20" open={wo.status === 'EXECUTION' || wo.status === 'PLANNING'}>
        <summary className="p-6 cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-bold text-text-primary flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-text-muted" /> Чек-лист шагов
            </h4>
            <span className="text-xs bg-bg-input px-2 py-0.5 rounded-full text-text-muted font-mono">{wo.items.length}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-text-muted transition-transform group-open:rotate-90" />
        </summary>
        
        <div className="px-6 pb-6">
          {wo.items.length === 0 && (
            <p className="text-sm text-text-muted italic">Шаги не добавлены. Используйте AI-ассистент или отредактируйте через черновик.</p>
          )}
          {wo.items.length > 0 && (
            <div className="space-y-3">
              {wo.items.map((item, idx) => {
                const meta = item.metadata_json || {}
                const isUnresolved = meta.catalog_match_confidence === 'none' ||
                  meta.rack_match_confidence === 'none' ||
                  !!meta.needs_clarification_reason
                const catalogLabel = meta.resolved_catalog_name || meta.equipment_name || ''
                const rackLabel = meta.rack_name || meta.rack_label || ''
                const uPos = meta.u_position || meta.position_u
                const isComplete = item.status === 'DONE' || item.status === 'SKIPPED'
                const isEditing = editingItemId === item.id
                const showEditor = !isComplete

                return (
                  <div
                    key={item.id}
                    className={`border rounded-xl transition-colors flex flex-col md:flex-row md:items-center justify-between p-3 gap-4 ${
                      item.status === 'DONE'
                        ? 'border-status-normal/20 bg-status-normal/5'
                        : item.status === 'SKIPPED'
                        ? 'border-border/30 bg-bg-input/30'
                        : isUnresolved
                        ? 'border-l-2 border-l-status-warning/60 border-status-warning/20 bg-status-warning/5'
                        : 'border-border/40 bg-bg-panel/60'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-bg-input/80 flex items-center justify-center font-bold text-text-secondary text-sm shrink-0 border border-border/40 shadow-sm mt-0.5">
                        {item.status === 'DONE' ? <CheckCircle2 className="w-5 h-5 text-status-normal" /> : idx + 1}
                      </div>
                      <div className="pt-0.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-lg leading-none">{ITEM_TYPE_ICONS[item.item_type] || '📝'}</span>
                          <p className={`text-sm font-semibold ${isComplete ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                            {item.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] text-text-muted px-1.5 py-0.5 bg-bg-input rounded border border-border/40">
                            {item.item_type}
                          </span>
                          {catalogLabel && (
                            <span className="text-[10px] text-text-secondary px-1.5 py-0.5 bg-bg-input rounded border border-border/40">
                              {catalogLabel}{uPos ? ` @ U${uPos}` : ''}{rackLabel ? ` / ${rackLabel}` : ''}
                            </span>
                          )}
                          {meta.catalog_match_confidence === 'partial' && (
                            <span className="text-[10px] text-status-warning font-semibold">⚠ частичное совпадение</span>
                          )}
                          {isUnresolved && (
                            <span className="flex items-center gap-1 text-[10px] text-status-critical font-semibold">
                              <AlertTriangle className="w-3 h-3" /> {meta.needs_clarification_reason || 'требует уточнения'}
                            </span>
                          )}
                        </div>
                        
                        {/* Inline editor inside the row */}
                        {showEditor && isEditing && (
                          <div className="mt-4 p-3 bg-bg-input/40 rounded-lg border border-status-warning/20">
                            <p className="text-xs text-status-warning font-semibold mb-3">
                              ⚠ Требует уточнения{meta.needs_clarification_reason ? `: ${meta.needs_clarification_reason}` : ''}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                              {(item.item_type === 'INSTALL_EQUIPMENT' && !meta.catalog_id) && (
                                <div>
                                  <label className="block text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1">Модель</label>
                                  <select
                                    value={editFields.catalog_id}
                                    onChange={e => setEditFields(f => ({ ...f, catalog_id: e.target.value }))}
                                    className="w-full text-sm px-3 py-2 rounded-lg bg-bg-input border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
                                  >
                                    <option value="">-- Выберите модель --</option>
                                    {catalogList?.map((c: any) => (
                                      <option key={c.id} value={c.id}>{c.manufacturer} {c.model}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              {!meta.rack_id && (
                                <div>
                                  <label className="block text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1">Стойка</label>
                                  <select
                                    value={editFields.rack_id}
                                    onChange={e => setEditFields(f => ({ ...f, rack_id: e.target.value }))}
                                    className="w-full text-sm px-3 py-2 rounded-lg bg-bg-input border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
                                  >
                                    <option value="">-- Выберите стойку --</option>
                                    {racksList?.map((r: any) => (
                                      <option key={r.id} value={r.id}>{r.name || r.label || `Стойка #${r.id}`}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              {item.item_type === 'INSTALL_EQUIPMENT' && (
                                <div>
                                  <label className="block text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1">U-позиция</label>
                                  <input
                                    type="number"
                                    min={1}
                                    value={editFields.u_position}
                                    onChange={e => setEditFields(f => ({ ...f, u_position: e.target.value }))}
                                    placeholder="U"
                                    className="w-full text-sm px-3 py-2 rounded-lg bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                                  />
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveItemMeta(item)}
                                disabled={saving}
                                className="text-xs font-semibold bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded-lg transition-colors active:scale-95 disabled:opacity-60"
                              >
                                {saving ? 'Сохранение...' : 'Сохранить'}
                              </button>
                              <button
                                onClick={() => setEditingItemId(null)}
                                className="text-xs font-semibold text-text-muted border border-border px-3 py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
                              >
                                Отмена
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 shrink-0 self-start md:self-center ml-[3.5rem] md:ml-0 mt-2 md:mt-0">
                      {showEditor && (
                        <button
                          onClick={() => isEditing ? setEditingItemId(null) : openItemEditor(item)}
                          className="text-xs font-semibold text-text-muted hover:text-text-primary border border-border/50 px-2 py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
                          title="Уточнить данные шага"
                        >
                          ✏
                        </button>
                      )}
                      <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider border ${
                        item.status === 'PENDING' ? 'bg-bg-input text-text-primary border-border/50' :
                        item.status === 'IN_PROGRESS' ? 'bg-accent/10 text-accent border-accent/20' :
                        item.status === 'BLOCKED' ? 'bg-status-critical/10 text-status-critical border-status-critical/20' :
                        item.status === 'NEEDS_CLARIFICATION' ? 'bg-status-warning/10 text-status-warning border-status-warning/20' :
                        item.status === 'DONE' ? 'bg-status-normal/10 text-status-normal border-status-normal/20' :
                        item.status === 'SKIPPED' ? 'bg-bg-input text-text-muted border-border' :
                        'bg-bg-input text-text-muted border-border'
                      }`}>
                        {item.status}
                      </span>
                      {wo.status === 'EXECUTION' && (
                        <>
                          {item.status === 'PENDING' && (
                            <>
                              <button
                                onClick={() => onUpdateItem(wo.id, item.id, 'IN_PROGRESS')}
                                className="text-xs font-semibold text-accent border border-accent/20 px-3 py-1.5 rounded-lg hover:bg-accent/10 transition-colors bg-accent/5"
                              >
                                В работу
                              </button>
                              <button
                                onClick={() => onUpdateItem(wo.id, item.id, 'BLOCKED')}
                                className="text-xs font-semibold text-status-critical border border-status-critical/20 px-3 py-1.5 rounded-lg hover:bg-status-critical/10 transition-colors bg-status-critical/5"
                              >
                                Блок
                              </button>
                              <button
                                onClick={() => onOpenConfirm(
                                  'Пропустить этот шаг? Действие будет отмечено как не выполненное.',
                                  () => onUpdateItem(wo.id, item.id, 'SKIPPED')
                                )}
                                title="Пропустить шаг (не выполнять)"
                                className="text-xs font-semibold text-text-muted border border-border px-3 py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
                              >
                                Пропустить
                              </button>
                            </>
                          )}
                          {item.status === 'IN_PROGRESS' && (
                            <>
                              <button
                                onClick={() => onOpenConfirm(
                                  'Отметить шаг как выполненный?',
                                  () => onUpdateItem(wo.id, item.id, 'DONE')
                                )}
                                className="text-xs font-semibold text-status-normal border border-status-normal/20 px-3 py-1.5 rounded-lg hover:bg-status-normal/10 transition-colors bg-status-normal/5"
                              >
                                Выполнено
                              </button>
                              <button
                                onClick={() => onUpdateItem(wo.id, item.id, 'BLOCKED')}
                                className="text-xs font-semibold text-status-critical border border-status-critical/20 px-3 py-1.5 rounded-lg hover:bg-status-critical/10 transition-colors bg-status-critical/5"
                              >
                                Блок
                              </button>
                            </>
                          )}
                          {item.status === 'BLOCKED' && (
                            <>
                              <button
                                onClick={() => onUpdateItem(wo.id, item.id, 'IN_PROGRESS')}
                                className="text-xs font-semibold text-accent border border-accent/20 px-3 py-1.5 rounded-lg hover:bg-accent/10 transition-colors bg-accent/5"
                              >
                                В работу
                              </button>
                              <button
                                onClick={() => onOpenConfirm(
                                  'Пропустить этот шаг? Действие будет отмечено как не выполненное.',
                                  () => onUpdateItem(wo.id, item.id, 'SKIPPED')
                                )}
                                title="Пропустить шаг (не выполнять)"
                                className="text-xs font-semibold text-text-muted border border-border px-3 py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
                              >
                                Пропустить
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </details>
    </div>
  )
}
